/**
 * usePbDashboardConfig — loads and persists the PB Dashboard widget config
 * via the existing GET/PUT /api/v2/admin/me/dashboard-config endpoint,
 * under the `pb_dashboard` sub-key.
 *
 * Debounced save (500 ms) like useDashboardConfig, so rapid toggles don't
 * flood the API.
 *
 * Extension (Punkt 4): `order` stores the widget display order; `dragEnabled`
 * toggles drag-and-drop mode. Both are persisted under `_meta`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  pbDashboardConfigApi,
  dashboardAreaApi,
  DASHBOARD_DEFAULTS_PERMISSION,
  type DashboardSource,
  type PbDashboardConfig,
  type PbDashboardWidgetId,
  type PbDashboardWidgetSettings,
  DEFAULT_PB_WIDGET_SETTINGS,
} from './api';

export type { PbDashboardWidgetId, PbDashboardWidgetSettings };

export const ALL_WIDGET_IDS: PbDashboardWidgetId[] = [
  'betreuungen', 'aufgaben', 'notizen', 'nachrichten', 'dokumente', 'medien', 'bewertungen', 'karte', 'matching', 'gespraechsprotokolle',
];

/** Größe eines Widgets im Grid: Spalten- (w) und Zeilen-Span (h), je 1..N. */
export interface WidgetSize {
  w: number;
  h: number;
}

export const DEFAULT_WIDGET_SIZE: WidgetSize = { w: 1, h: 1 };

/** Grenzen für den Resize (Grid-Spannen), um Layout-Ausreißer zu verhindern. */
export const MAX_WIDGET_SPAN = 4;

/**
 * `_meta` in api.ts (PbDashboardMeta) modelliert bislang nur order+dragEnabled.
 * resizeEnabled + sizes werden additiv im selben `_meta`-Objekt persistiert;
 * dieser lokale Typ erweitert das Meta um die neuen Felder (Punkt 2).
 */
interface PbDashboardMetaExt {
  order?: PbDashboardWidgetId[];
  dragEnabled?: boolean;
  /** Ob der Größen-Verstellmodus in den Einstellungen aktiviert ist. */
  resizeEnabled?: boolean;
  /** Pro-Widget-Größe (Grid-Span). Fehlende Einträge → DEFAULT_WIDGET_SIZE. */
  sizes?: Partial<Record<PbDashboardWidgetId, WidgetSize>>;
}

/**
 * Bereichs-Dashboard (Paket 2310e). Auflösung: User-Layout > Bereichs-Standard
 * > eingebauter Default.
 *   scope 'user'    Änderungen gelten nur für mich (Standard).
 *   scope 'default' Änderungen werden als Standard für alle im Bereich PB
 *                   gespeichert (nur mit Recht menu.einstellungen).
 */
export interface DashboardAreaScope {
  /** Woher das aktive Layout stammt. */
  source: DashboardSource;
  scope: 'user' | 'default';
  setScope: (scope: 'user' | 'default') => void;
  /** Darf der Admin den Bereichs-Standard setzen? */
  canSetDefault: boolean;
  /** Eigenes Layout verwerfen (Bereichs-Standard bzw. eingebauter Default greift wieder). */
  resetToDefault: () => Promise<void>;
  /** Bereichs-Standard für alle entfernen (nur Scope 'default'). */
  clearDefault: () => Promise<void>;
}

export interface UsePbDashboardConfigReturn {
  /** Bereichs-Auswahl "Nur für mich" / "Standard für alle" + Zurücksetzen. */
  areaScope: DashboardAreaScope;
  config: PbDashboardConfig;
  loading: boolean;
  /** Effective widget order (merges stored + any new IDs not yet in order). */
  widgetOrder: PbDashboardWidgetId[];
  /** Whether drag-reorder is currently enabled. */
  dragEnabled: boolean;
  /** Get settings for a single widget (falls back to default). */
  widgetSettings: (id: PbDashboardWidgetId) => PbDashboardWidgetSettings;
  /** Toggle visibility or change mode for a widget; persists after debounce. */
  setWidgetSettings: (id: PbDashboardWidgetId, patch: Partial<PbDashboardWidgetSettings>) => void;
  /** Persist a new widget order. */
  setWidgetOrder: (order: PbDashboardWidgetId[]) => void;
  /** Toggle drag mode on/off; persisted. */
  setDragEnabled: (enabled: boolean) => void;
  /** Whether size-resize mode is currently enabled. */
  resizeEnabled: boolean;
  /** Toggle resize mode on/off; persisted. */
  setResizeEnabled: (enabled: boolean) => void;
  /** Get the grid size (span) for a single widget (falls back to default). */
  widgetSize: (id: PbDashboardWidgetId) => WidgetSize;
  /** Persist a new size for a single widget (debounced). */
  setWidgetSize: (id: PbDashboardWidgetId, size: WidgetSize) => void;
}

export function usePbDashboardConfig(): UsePbDashboardConfigReturn {
  const [config, setConfig] = useState<PbDashboardConfig>({});
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [source, setSource] = useState<DashboardSource>('builtin');
  const [scope, setScopeState] = useState<'user' | 'default'>('user');
  const scopeRef = useRef<'user' | 'default'>('user');
  const { hasPermission } = usePermissions();
  const canSetDefault = hasPermission(DASHBOARD_DEFAULTS_PERMISSION);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stored = await pbDashboardConfigApi.getWithSource();
        if (alive) { setConfig(stored.config); setSource(stored.source); }
      } catch {
        // Non-fatal — use default empty config
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const scheduleSave = useCallback((next: PbDashboardConfig) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (scopeRef.current === 'default') {
        dashboardAreaApi.setDefault('pb', next as Record<string, unknown>).catch(() => { /* silent */ });
      } else {
        pbDashboardConfigApi.save(next)
          .then(() => setSource('user'))
          .catch(() => { /* silent */ });
      }
    }, 500);
  }, []);

  const setScope = useCallback((nextScope: 'user' | 'default') => {
    if (nextScope === 'default' && !canSetDefault) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    scopeRef.current = nextScope;
    setScopeState(nextScope);
    void (async () => {
      try {
        if (nextScope === 'default') {
          // Im Standard-Modus wird der Bereichs-Standard bearbeitet (nicht das eigene Layout).
          const def = await dashboardAreaApi.getDefault('pb');
          if (scopeRef.current === 'default') setConfig((def as PbDashboardConfig | null) ?? {});
        } else {
          const stored = await pbDashboardConfigApi.getWithSource();
          if (scopeRef.current === 'user') { setConfig(stored.config); setSource(stored.source); }
        }
      } catch { /* silent */ }
    })();
  }, [canSetDefault]);

  const resetToDefault = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const res = await dashboardAreaApi.reset('pb');
    setConfig((res.config.pb_dashboard as PbDashboardConfig | undefined) ?? {});
    setSource(res.source);
  }, []);

  const clearDefault = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await dashboardAreaApi.clearDefault('pb');
    setConfig({});
  }, []);

  const widgetSettings = useCallback((id: PbDashboardWidgetId): PbDashboardWidgetSettings => {
    return config[id] ?? { ...DEFAULT_PB_WIDGET_SETTINGS };
  }, [config]);

  const setWidgetSettings = useCallback((
    id: PbDashboardWidgetId,
    patch: Partial<PbDashboardWidgetSettings>,
  ) => {
    setConfig(prev => {
      const existing = prev[id] ?? { ...DEFAULT_PB_WIDGET_SETTINGS };
      const next = {
        ...prev,
        [id]: { ...existing, ...patch },
      };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  // Effective order: stored order merged with any new widget IDs not yet in it.
  const widgetOrder: PbDashboardWidgetId[] = (() => {
    const stored = config._meta?.order ?? [];
    const merged = [...stored];
    for (const id of ALL_WIDGET_IDS) {
      if (!merged.includes(id)) merged.push(id);
    }
    return merged;
  })();

  const meta = (config._meta ?? {}) as PbDashboardMetaExt;
  const dragEnabled = meta.dragEnabled ?? false;
  const resizeEnabled = meta.resizeEnabled ?? false;

  const setWidgetOrder = useCallback((order: PbDashboardWidgetId[]) => {
    setConfig(prev => {
      const next: PbDashboardConfig = {
        ...prev,
        _meta: { ...(prev._meta ?? {}), order },
      };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const setDragEnabled = useCallback((enabled: boolean) => {
    setConfig(prev => {
      const next: PbDashboardConfig = {
        ...prev,
        _meta: { ...(prev._meta ?? {}), dragEnabled: enabled },
      };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const setResizeEnabled = useCallback((enabled: boolean) => {
    setConfig(prev => {
      const next: PbDashboardConfig = {
        ...prev,
        _meta: { ...(prev._meta ?? {}), resizeEnabled: enabled } as PbDashboardMetaExt,
      };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const widgetSize = useCallback((id: PbDashboardWidgetId): WidgetSize => {
    const stored = (config._meta as PbDashboardMetaExt | undefined)?.sizes?.[id];
    return stored ? { ...DEFAULT_WIDGET_SIZE, ...stored } : { ...DEFAULT_WIDGET_SIZE };
  }, [config]);

  const setWidgetSize = useCallback((id: PbDashboardWidgetId, size: WidgetSize) => {
    // Auf sinnvolle Spannen begrenzen (1..MAX_WIDGET_SPAN).
    const clamp = (n: number) => Math.min(MAX_WIDGET_SPAN, Math.max(1, Math.round(n)));
    const clamped: WidgetSize = { w: clamp(size.w), h: clamp(size.h) };
    setConfig(prev => {
      const prevMeta = (prev._meta ?? {}) as PbDashboardMetaExt;
      const next: PbDashboardConfig = {
        ...prev,
        _meta: {
          ...prevMeta,
          sizes: { ...(prevMeta.sizes ?? {}), [id]: clamped },
        } as PbDashboardMetaExt,
      };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  return {
    areaScope: { source, scope, setScope, canSetDefault, resetToDefault, clearDefault },
    config, loading, widgetOrder, dragEnabled, widgetSettings, setWidgetSettings,
    setWidgetOrder, setDragEnabled,
    resizeEnabled, setResizeEnabled, widgetSize, setWidgetSize,
  };
}
